import * as React from 'react';
import {Suspense} from 'react';
import {Route, Routes as ReactRouterRoutes} from 'react-router-dom';

import {ChromeHeaderLayout} from 'common/report/layout';

import {GlobalLoader} from './components/global-loader';

const lazy = <T extends React.ComponentType<any>>(dynamicImport: () => Promise<{default: T}>) => (
  <Suspense fallback={<GlobalLoader />}>{React.createElement(React.lazy(dynamicImport))}</Suspense>
);

export const Routes = () => {
  return (
    <ReactRouterRoutes>
      <Route
        path='/public/:uuid'
        element={lazy(
          () => import(/* webpackChunkName: "public-boards" */ `apps/boards/src/dashboards-app/public-mode/app`),
        )}
      />
      <Route
        path='/dashboard-digest'
        element={lazy(
          () => import(/* webpackChunkName: "digest-boards" */ `apps/boards/src/dashboards-app/digest-mode/app`),
        )}
      />
      {/* workspace id could be omitted */}
      {/* currently it's being handled in the header (in Layout) */}
      {/* https://github.com/mixpanel/analytics/blob/3ba772707e8f91ccac2c4920527d37ed94de21c6/iron/common/widgets/chrome/chrome-header/index.ts#L1790-L1808 */}
      <Route path='/project/:projectId/view?/:workspaceId?/app' element={<ChromeHeaderLayout />}>
        <Route element={lazy(() => import(`common/widgets/side-nav`))}>
          <Route path='home/*' element={lazy(() => import(/* webpackChunkName: "home" */ `apps/home/src`))} />
          {/* Core apps and board goes on the top */}
          <Route
            path='boards/embed?/*'
            element={lazy(
              () =>
                import(
                  /* webpackChunkName: "boards" */
                  /* webpackPreload: true */
                  `apps/boards/src`
                ),
            )}
          />
          <Route
            path='insights/embed?/*'
            element={lazy(
              () =>
                import(
                  /* webpackChunkName: "core-report" */
                  /* webpackPreload: true */
                  `apps/insights/src`
                ),
            )}
          />
          <Route
            path='funnels/embed?/*'
            element={lazy(
              () =>
                import(
                  /* webpackChunkName: "core-report" */
                  /* webpackPreload: true */
                  `apps/insights/src`
                ),
            )}
          />
          <Route
            path='flows/embed?/*'
            element={lazy(
              () =>
                import(
                  /* webpackChunkName: "core-report" */
                  /* webpackPreload: true */
                  `apps/flows/src`
                ),
            )}
          />
          <Route
            path='retention/embed?/*'
            element={lazy(
              () =>
                import(
                  /* webpackChunkName: "core-report" */
                  /* webpackPreload: true */
                  `apps/insights/src`
                ),
            )}
          />
        </Route>
        {/* User & Event */}
        <Route
          path='events/embed?/*'
          element={lazy(() => import(/* webpackChunkName: "events" */ `apps/events/src`))}
        />
        <Route path='users/embed?/*' element={lazy(() => import(/* webpackChunkName: "users" */ `apps/users/src`))} />
        <Route
          path='profile/embed?/*'
          element={lazy(() => import(/* webpackChunkName: "profile" */ `apps/profile/src`))}
        />

        {/* data management apps */}
        <Route path='lexicon/*' element={lazy(() => import(/* webpackChunkName: "lexicon" */ `apps/lexicon/src`))} />
        {/* currently used for cohorts */}
        <Route
          path='entity-management/*'
          element={lazy(() => import(/* webpackChunkName: "entity-management" */ `apps/entity-management/src`))}
        />
        <Route
          path='integrations/*'
          element={lazy(() => import(/* webpackChunkName: "integrations" */ `apps/integrations/src`))}
        />

        {/* apps in the app dropdown menu */}
        <Route
          path='signal/embed?/*'
          element={lazy(() => import(/* webpackChunkName: "signal" */ `apps/signal/src`))}
        />
        <Route
          path='impact/embed?/*'
          element={lazy(() => import(/* webpackChunkName: "impact" */ `apps/impact/src`))}
        />
        <Route
          path='experiment-reporting/embed?/*'
          element={lazy(() => import(/* webpackChunkName: "experiment-reporting" */ `apps/experiment-reporting/src`))}
        />
        <Route
          path='jql-console/embed?/*'
          element={lazy(() => import(/* webpackChunkName: "jql-console" */ `apps/jql-console/src`))}
        />

        {/* settings related apps */}
        <Route path='views/*' element={lazy(() => import(/* webpackChunkName: "views" */ `apps/views/src`))} />
        <Route path='settings/*' element={lazy(() => import(/* webpackChunkName: "settings" */ `apps/settings/src`))} />
      </Route>
      <Route
        path='/organization/*'
        element={lazy(() => import(/* webpackChunkName: "admin-org-settings" */ `apps/admin-org-settings/src/index`))}
      />
    </ReactRouterRoutes>
  );
};
